import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ChevronRight, Compass, Search, X } from "lucide-react";
import { Input } from "../ui/input";
import HelpMarkdown from "./HelpMarkdown";
import { HELP_AREAS } from "../../help/articles";
import {
  areaTitle,
  articlesInArea,
  getArticle,
  resolveTopic,
  searchHelp,
  splitSections,
} from "../../help/helpIndex";
import { markHelpOpened, onOpenHelp } from "../../utils/helpBus";
import { startTour } from "../../utils/tourBus";

const ArticleRow = ({ title, subtitle, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="grid w-full grid-cols-[minmax(0,1fr)_1rem] items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-muted/40"
  >
    <span className="min-w-0">
      <span className="block text-sm font-semibold text-white">{title}</span>
      {subtitle && (
        <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{subtitle}</span>
      )}
    </span>
    <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
  </button>
);

/**
 * The help centre: search and contents, then one article at a time. Opened
 * from the ? in the top bar, the account menu, or a "Full article" link.
 */
const HelpCentre = () => {
  const [open, setOpen] = useState(false);
  const [articleId, setArticleId] = useState(null);
  const [sectionId, setSectionId] = useState(null);
  const [query, setQuery] = useState("");
  const scrollRef = useRef(null);

  useEffect(
    () =>
      onOpenHelp((topic) => {
        const { article, section } = resolveTopic(topic);
        setArticleId(article?.id || null);
        setSectionId(section?.id || null);
        setQuery("");
        setOpen(true);
        markHelpOpened();
      }),
    [],
  );

  // Land on the requested section, or the top of whatever opened.
  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      const target = sectionId && document.getElementById(`help-${sectionId}`);
      if (target) target.scrollIntoView({ block: "start" });
      else if (scrollRef.current) scrollRef.current.scrollTop = 0;
    });
  }, [open, articleId, sectionId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const results = useMemo(() => searchHelp(query), [query]);
  const article = articleId ? getArticle(articleId) : null;
  const parts = useMemo(() => (article ? splitSections(article) : null), [article]);

  if (!open) return null;

  const show = (id, section = null) => {
    setArticleId(id);
    setSectionId(section);
  };

  let body;
  if (article) {
    body = (
      <article className="grid gap-5">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-primary">
            {areaTitle(article.area)}
          </p>
          <h2 className="mt-1 text-[24px] font-bold leading-tight text-white [text-wrap:balance]">
            {article.title}
          </h2>
        </div>
        {parts.intro && <HelpMarkdown content={parts.intro} />}
        {parts.sections.map((s) => (
          <section key={s.id} id={`help-${s.id}`} className="scroll-mt-4">
            <h3 className="mb-2 text-[17px] font-bold text-white">{s.title}</h3>
            <HelpMarkdown content={s.body} />
          </section>
        ))}
        {article.related?.length > 0 && (
          <div className="grid gap-2 border-t border-border pt-4">
            <p className="text-xs font-semibold text-muted-foreground">Related</p>
            <div className="overflow-hidden rounded-2xl border border-border bg-card divide-y divide-border">
              {article.related.map((id) => {
                const rel = getArticle(id);
                return rel ? (
                  <ArticleRow key={id} title={rel.title} subtitle={rel.summary} onClick={() => show(id)} />
                ) : null;
              })}
            </div>
          </div>
        )}
      </article>
    );
  } else {
    body = (
      <div className="grid gap-5">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search help"
            aria-label="Search help"
            className="h-11 bg-card pl-9 text-base"
          />
        </div>
        {query.trim() ? (
          results.length > 0 ? (
            <div className="overflow-hidden rounded-2xl border border-border bg-card divide-y divide-border">
              {results.map(({ article: a, section }) => (
                <ArticleRow
                  key={a.id}
                  title={section ? `${a.title}: ${section.title}` : a.title}
                  subtitle={a.summary}
                  onClick={() => show(a.id, section?.id || null)}
                />
              ))}
            </div>
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nothing matches "{query.trim()}". Try a different word.
            </p>
          )
        ) : (
          <>
          {/* The guided tour (#160): opt-in, from here. */}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              startTour("basics");
            }}
            className="flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/10 px-4 py-3 text-left"
          >
            <Compass className="h-5 w-5 shrink-0 text-primary" />
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-foreground">Show me around</span>
              <span className="block text-xs text-muted-foreground">A quick tour of the main screens</span>
            </span>
          </button>
          {HELP_AREAS.map((area) => (
            <div key={area.id} className="grid gap-2">
              <p className="text-xs font-semibold text-muted-foreground">{area.title}</p>
              <div className="overflow-hidden rounded-2xl border border-border bg-card divide-y divide-border">
                {articlesInArea(area.id).map((a) => (
                  <ArticleRow key={a.id} title={a.title} subtitle={a.summary} onClick={() => show(a.id)} />
                ))}
              </div>
            </div>
          ))}
          </>
        )}
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Help"
      className="fixed inset-0 z-[55] flex flex-col bg-background pt-[env(safe-area-inset-top)]"
    >
      <div className="border-b border-border">
        <div className="mx-auto grid w-full max-w-2xl grid-cols-[2.5rem_minmax(0,1fr)_2.5rem] items-center px-2 py-2">
          {article ? (
            <button
              type="button"
              onClick={() => show(null)}
              aria-label="Back to help contents"
              className="grid h-10 w-10 place-items-center rounded-lg text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          ) : (
            <span />
          )}
          <p className="text-center text-base font-semibold text-white">Help</p>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close help"
            className="grid h-10 w-10 place-items-center rounded-lg text-muted-foreground hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl px-4 pb-[calc(2rem+env(safe-area-inset-bottom))] pt-5">
          {body}
        </div>
      </div>
    </div>
  );
};

export default HelpCentre;
